<template>
    <div
        class="lg:col-span-3 hidden lg:flex flex-col h-screen overflow-hidden bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-gray-700">

        <!-- Header avec dégradé - Réduit sur petits écrans -->
        <div
            class="relative bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-6 py-3 shadow-md flex-shrink-0">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <div class="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <i class="las la-ticket-alt text-xl text-white"></i>
                    </div>
                    <h2 class="text-lg font-bold text-white">Gestion des Tickets</h2>
                </div>

                <!-- Badge statut (optionnel) -->
                <div v-if="isCreated"
                    class="px-2 py-0.5 bg-green-500 rounded-full text-white text-xs font-medium animate-pulse">
                    <i class="las la-check-circle mr-1"></i>
                    Créé
                </div>
            </div>
        </div>

        <!-- Profil du contact - Compact -->
        <div
            class="bg-gradient-to-b from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div class="flex items-center gap-4">
                <!-- Avatar réduit -->
                <div class="relative group flex-shrink-0">
                    <div
                        class="absolute inset-0 bg-blue-500 rounded-full blur-xl opacity-20 group-hover:opacity-30 transition-opacity">
                    </div>
                    <div class="relative">
                        <img v-if="conversation?.profile && !imageError" :src="conversation.profile"
                            @error="imageError = true" alt="Avatar"
                            class="w-16 h-16 rounded-full object-cover border-3 border-white dark:border-slate-700 shadow-lg" />
                        <div v-else
                            class="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center border-3 border-white dark:border-slate-700 shadow-lg">
                            <i class="las la-user text-2xl text-white"></i>
                        </div>

                        <!-- Badge en ligne -->
                        <div
                            class="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white dark:border-slate-700 rounded-full">
                        </div>
                    </div>
                </div>

                <!-- Infos contact compactes -->
                <div class="flex-1 min-w-0">
                    <h3 class="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
                        {{ conversation?.name ?? conversation?.chat_id?.split("@")[0] ?? 'Contact' }}
                    </h3>
                    <p v-if="conversation?.name" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        <i class="las la-phone mr-0.5"></i>
                        {{ conversation?.chat_id?.split("@")[0] ?? '—' }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Contenu scrollable - Prend tout l'espace restant -->
        <div
            class="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">

            <!-- Loading state -->
            <div v-if="isLoading" class="flex flex-col items-center justify-center h-full gap-4">
                <div class="relative">
                    <div
                        class="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin">
                    </div>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">Chargement des catégories...</p>
            </div>

            <!-- Error state -->
            <div v-else-if="!isSuccess" class="flex flex-col items-center justify-center h-full gap-4 px-6">
                <div class="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                    <i class="las la-exclamation-triangle text-3xl text-red-500 dark:text-red-400"></i>
                </div>
                <div class="text-center">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        Erreur de chargement
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Impossible de récupérer les catégories
                    </p>
                    <button @click="getCategories"
                        class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200">
                        <i class="las la-redo-alt mr-1"></i>
                        Réessayer
                    </button>
                </div>
            </div>

            <!-- Success message -->
            <div v-else-if="isCreated" class="p-6">
                <div
                    class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 text-center">
                    <div
                        class="w-16 h-16 bg-green-100 dark:bg-green-800/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i class="las la-check-circle text-4xl text-green-600 dark:text-green-400"></i>
                    </div>
                    <h3 class="text-lg font-bold text-green-900 dark:text-green-100 mb-2">
                        Ticket créé avec succès !
                    </h3>
                    <p class="text-sm text-green-700 dark:text-green-300 mb-4">
                        Ticket #{{ ticket?.id ?? createItem?.id }}
                    </p>
                    <button @click="resetForm"
                        class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors duration-200">
                        <i class="las la-plus mr-1"></i>
                        Créer un nouveau ticket
                    </button>
                </div>
            </div>

            <!-- Formulaire -->
            <div v-else-if="raisons.length" class="p-5 space-y-5">


                <!-- Section Type de Ticket -->
                <section
                    class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                    <div class="flex items-center gap-2 mb-3">
                        <div
                            class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i class="las la-cog text-lg text-blue-600 dark:text-blue-400"></i>
                        </div>
                        <div>
                            <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Type de Ticket
                            </h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                Sélectionnez le type de ticket
                            </p>
                        </div>
                    </div>

                    <div class="relative">
                        <select v-model="ticketType" @focus="ticketTypeFocused = true" @blur="ticketTypeFocused = false"
                            :class="[
                                'w-full px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-slate-700 border-2 rounded-lg outline-none appearance-none cursor-pointer transition-all duration-200',
                                ticketTypeFocused
                                    ? 'border-blue-500 bg-white dark:bg-slate-700 shadow-sm'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            ]">
                            <option value="" disabled selected>Choisissez un type...</option>
                            <option value="1">Incident</option>
                            <option value="2">Demande</option>
                        </select>

                        <!-- Icône de dropdown personnalisée -->
                        <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <i class="las la-chevron-down text-gray-400 dark:text-gray-500 transition-transform duration-200"
                                :class="{ 'rotate-180': ticketTypeFocused }"></i>
                        </div>
                    </div>

                    <!-- Message si type sélectionné -->
                    <div v-if="ticketType"
                        class="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div class="flex items-start gap-2">
                            <i
                                class="las la-info-circle text-blue-600 dark:text-blue-400 text-sm mt-0.5 flex-shrink-0"></i>
                            <div class="flex-1 min-w-0">
                                <p class="text-xs font-medium text-blue-900 dark:text-blue-100">
                                    Type sélectionné
                                </p>
                                <p class="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                                    {{ ticketType === '1' ? 'Incident' : 'Demande' }}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Section Catégorie avec recherche -->
                <section
                    class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                    <div class="flex items-center gap-2 mb-3">
                        <div
                            class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i class="las la-list text-lg text-blue-600 dark:text-blue-400"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Catégorie du ticket
                            </h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                {{ filteredRaisons.length }} catégorie(s)
                            </p>
                        </div>
                    </div>

                    <!-- Barre de recherche de catégories -->
                    <div class="relative mb-3">
                        <div :class="[
                            'flex items-center bg-gray-50 dark:bg-slate-700/50 rounded-lg px-3 py-2 border-2 transition-all duration-200',
                            searchFocused
                                ? 'border-blue-500 bg-white dark:bg-slate-700 shadow-sm'
                                : 'border-transparent hover:bg-gray-100 dark:hover:bg-slate-700'
                        ]">
                            <i :class="[
                                'las la-search text-lg mr-2 transition-colors duration-200 flex-shrink-0',
                                searchFocused || searchQuery
                                    ? 'text-blue-500 dark:text-blue-400'
                                    : 'text-gray-400 dark:text-gray-500'
                            ]"></i>
                            <input v-model="searchQuery" @focus="searchFocused = true" @blur="searchFocused = false"
                                type="text" placeholder="Rechercher..."
                                class="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-300 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500" />
                            <button v-if="searchQuery" @click="searchQuery = ''"
                                class="ml-2 w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center transition-all duration-200 flex-shrink-0">
                                <i class="las la-times text-xs text-gray-600 dark:text-gray-300"></i>
                            </button>
                        </div>

                        <!-- Indicateur de résultats -->
                        <div v-if="searchQuery && filteredRaisons.length === 0"
                            class="absolute top-full mt-2 left-0 right-0 z-10">
                            <div
                                class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-center">
                                <i class="las la-search text-2xl text-yellow-600 dark:text-yellow-400 mb-1"></i>
                                <p class="text-xs text-yellow-700 dark:text-yellow-300">
                                    Aucune catégorie trouvée
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Grille des catégories filtrées - Hauteur adaptative -->
                    <div
                        class="space-y-2 max-h-48 overflow-y-auto overflow-x-hidden pr-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                        <div v-for="item in filteredRaisons" :key="item.id" @click="selectCategory(item)" :class="[
                            'group relative p-3 rounded-lg border-2 cursor-pointer transition-all duration-200',
                            currentItem?.completename === item.completename
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md scale-[1.01]'
                                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-700/50 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm hover:scale-[1.00]'
                        ]">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-2 flex-1 min-w-0">
                                    <!-- Radio personnalisé -->
                                    <div :class="[
                                        'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200',
                                        currentItem?.completename === item.completename
                                            ? 'border-blue-500 bg-blue-500'
                                            : 'border-gray-300 dark:border-gray-600 group-hover:border-blue-400'
                                    ]">
                                        <div v-if="currentItem?.completename === item.completename"
                                            class="w-1.5 h-1.5 bg-white rounded-full"></div>
                                    </div>

                                    <span :class="[
                                        'text-sm font-medium truncate',
                                        currentItem?.completename === item.completename
                                            ? 'text-blue-700 dark:text-blue-300'
                                            : 'text-gray-700 dark:text-gray-300'
                                    ]" v-html="highlightMatch(item.completename)"></span>
                                </div>

                                <!-- Icône check -->
                                <i v-if="currentItem?.completename === item.completename"
                                    class="las la-check-circle text-lg text-blue-500 flex-shrink-0 ml-2"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Message si catégorie sélectionnée -->
                    <div v-if="currentItem"
                        class="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div class="flex items-start gap-2">
                            <i
                                class="las la-info-circle text-blue-600 dark:text-blue-400 text-sm mt-0.5 flex-shrink-0"></i>
                            <div class="flex-1 min-w-0">
                                <p class="text-xs font-medium text-blue-900 dark:text-blue-100">
                                    Sélectionnée
                                </p>
                                <p class="text-xs text-blue-700 dark:text-blue-300 mt-0.5 truncate">
                                    {{ currentItem.completename }}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>



                <!-- Section Description -->
                <section
                    class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                    <div class="flex items-center gap-2 mb-3">
                        <div
                            class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i class="las la-pen text-lg text-blue-600 dark:text-blue-400"></i>
                        </div>
                        <div>
                            <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Description
                            </h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                Minimum 10 caractères
                            </p>
                        </div>
                    </div>

                    <div class="relative">
                        <textarea v-model="description" @focus="descriptionFocused = true"
                            @blur="descriptionFocused = false" rows="4" :class="[
                                'w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-slate-700 border-2 rounded-lg outline-none resize-none transition-all duration-200',
                                descriptionFocused
                                    ? 'border-blue-500 bg-white dark:bg-slate-700 shadow-sm'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            ]" placeholder="Décrivez votre demande..."></textarea>

                        <!-- Compteur de caractères avec validation -->
                        <div class="absolute bottom-2 right-3 flex items-center gap-1">
                            <i v-if="description.length >= 10" class="las la-check-circle text-green-500 text-sm"></i>
                            <span :class="[
                                'text-xs font-medium',
                                description.length < 10
                                    ? 'text-red-500'
                                    : description.length < 50
                                        ? 'text-yellow-500'
                                        : 'text-green-500'
                            ]">
                                {{ description.length }}/10
                            </span>
                        </div>
                    </div>
                </section>

                <!-- Boutons d'action - Sticky en bas -->
                <div class="sticky bottom-0 bg-white dark:bg-slate-900 pt-3 pb-1 space-y-2">
                    <!-- Bouton principal -->
                    <button @click="preapreBeforeSubmit(true)" :disabled="!canSubmit || createIsLoading" :class="[
                        'w-full py-2.5 text-white text-sm font-semibold rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2',
                        canSubmit && !createIsLoading
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl cursor-pointer'
                            : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
                    ]">
                        <i v-if="!createIsLoading" class="las la-ticket-alt text-base"></i>
                        <div v-else
                            class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{{ createIsLoading ? 'Création...' : 'Créer ticket' }}</span>
                    </button>

                    <!-- Bouton secondaire -->
                    <button @click="preapreBeforeSubmit(false)" :disabled="!canSubmit || createIsLoading" :class="[
                        'w-full py-2 text-sm font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2',
                        canSubmit && !createIsLoading
                            ? 'bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 cursor-pointer'
                            : 'bg-gray-100 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60'
                    ]">
                        <i class="las la-save text-base"></i>
                        <span>Enregistrer simplement</span>
                    </button>
                </div>

            </div>

        </div>
    </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapActions } from 'vuex';
import notify from '@/utils/notify';

export default {
    name: "ContactIndex",

    data() {
        return {
            description: "",
            ticket: "",
            imageError: false,
            searchQuery: "",
            searchFocused: false,
            descriptionFocused: false,
            ticketType: "",
            ticketTypeFocused: false,
        };
    },

    computed: {
        ...mapFields("tickets/list", {
            isLoading: "isLoading",
            raisons: "items",
            options: "options",
            currentItem: "currentItem",
            isSuccess: "isSuccess",
            messageAlert: "message",
            showMessage: "showMessage",
        }),
        ...mapFields("tickets/create", {
            isCreated: "isCreated",
            createIsLoading: "isLoading",
            createItem: "item",
            createIsSuccess: "isSuccess",
            createMessage: "message",
            createShowMessage: "showMessage",
        }),
        ...mapFields("conversations/show", {
            conversation: "item",
        }),

        filteredRaisons() {
            if (!this.searchQuery.trim()) {
                return this.raisons;
            }
            const query = this.searchQuery.toLowerCase();
            return this.raisons.filter(raison =>
                raison.completename?.toLowerCase().includes(query)
            );
        },

        canSubmit() {
            return this.currentItem && this.description.trim().length >= 10;
        }
    },

    methods: {
        ...mapActions({
            getCategories: "tickets/list/getAll",
            createTicket: "tickets/create/create",
            saveOnly: "tickets/create/saveOnly",
            resetTicket: "tickets/create/resetData",
        }),

        selectCategory(item) {
            this.currentItem = item;
            this.$nextTick(() => {
                const descSection = document.querySelector('textarea');
                if (descSection) {
                    descSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },

        highlightMatch(text) {
            if (!this.searchQuery.trim()) return text;
            const regex = new RegExp(`(${this.searchQuery})`, 'gi');
            return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded">$1</mark>');
        },

        preapreBeforeSubmit(isCreatedTicket = true) {
            if (!this.currentItem) {
                notify("Le choix d'une catégorie est obligatoire", "warning");
                return;
            }

            if (this.description.trim().length < 10) {
                notify("La description doit contenir au moins 10 caractères", "warning");
                return;
            }

            const data = {
                ...this.currentItem,
                conversation_id: this.conversation?.id,
                category_id: this.currentItem?.id,
                reason: this.currentItem?.completename,
                description: this.description.trim(),
                ticket_type: this.ticketType,
            };

            if (isCreatedTicket) {
                this.createTicket(data);
            } else {
                this.saveOnly(data);
            }
        },

        resetForm() {
            this.resetTicket();
            this.description = "";
            this.searchQuery = "";
            this.currentItem = null;
            this.ticketType = "";
        }
    },

    watch: {
        conversation() {
            this.resetForm();
            this.imageError = false;
        },

        isCreated(val) {
            if (val) {
                this.ticket = this.createItem;
            }
        }
    },

    mounted() {
        this.getCategories();
    },
};
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar {
    width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
    @apply bg-gray-300 dark:bg-gray-600 rounded-full;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400 dark:bg-gray-500;
}

mark {
    animation: highlight 0.3s ease-in-out;
}

@keyframes highlight {
    from {
        background-color: transparent;
    }

    to {
        background-color: rgb(254 240 138);
    }
}
</style>